import { Card } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  useAdditionalTeachingLoadsListQuery,
  useListPlannedTeacherTeachingLoadQuery,
} from '../../../types/planung-graphql-client-defs';
import { groupByMapArray } from '../../../utils/arrayFunc';
import { formatNumber } from '../../../utils/helper';
import { DeputateDetailsOverviewData, DeputatesDetailTableData } from '../Overview/DeputateDetailsTeacherOverview';
import styles from './EvaluatePlannedDeputateStats.module.scss';

export const EvaluatePlannedDeputateStats = observer(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

  const [{ data: deputateData }] = useListPlannedTeacherTeachingLoadQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: currentSchoolYear?.uuid ?? '',
    },
    pause: !currentSchoolYear || currentSchoolYear.uuid === '',
  });

  const [{ data: additionalTeachingLoadQueryResult }] = useAdditionalTeachingLoadsListQuery({
    variables: {
      schoolyear: currentSchoolYear?.uuid ?? '',
    },
    context,
    pause: !currentSchoolYear || currentSchoolYear.uuid === '',
  });

  const memoizedStats: DeputateDetailsOverviewData = useMemo(() => {
    const deputatData: Array<DeputatesDetailTableData> = deputateData?.plannedTeachingLoadByTeacher ?? [];
    const groupedData = groupByMapArray<string, DeputatesDetailTableData>(
      deputatData,
      (lesson) => lesson.subjectContainerDisplayName ?? '',
    );

    const subjectLessons = groupedData.get('');
    groupedData.delete('');
    const subjectContainerLessons = groupedData;

    const additional =
      additionalTeachingLoadQueryResult?.additionalTeachingLoads.map(
        (additional) =>
          ({
            comment: additional.comment,
            teacherUuid: additional.teacher.uuid ?? '',
            teacherDisplayName: additional.teacherListName ?? '',
            subjectUuid: additional.subject?.uuid ?? '',
            subjectDisplayName: additional.subject?.name ?? '',
            partialClasses: false,
            lessonClassesDisplayName: additional.class?.name ?? '',
            countClasses: additional.class ? 1 : 0,
            countTeacher: 0,
            studentCards: 0,
            teacherCards: 0,
            studentHours: 0,
            teacherHours: 0,
            teachingBlockWeeks: 0,
            teachingBlocks: 0,
            factor: 0,
            deputat: additional.hours,
          }) as DeputatesDetailTableData,
      ) ?? [];

    const sumSubjectLessons = subjectLessons?.map((d) => d.deputat ?? 0).reduce((acc, cur) => acc + cur) ?? 0;
    const sumSubjectContainerLessons = new Map<string, number>();
    const sumAdditional = additional?.map((d) => d.deputat ?? 0).reduce((acc, cur) => acc + cur, 0) ?? 0;

    let sumTotal = sumSubjectLessons + sumAdditional;

    subjectContainerLessons.forEach((value, key) => {
      sumSubjectContainerLessons.set(key, value.map((d) => d.deputat ?? 0).reduce((acc, cur) => acc + cur, 0) ?? 0);
      sumTotal += sumSubjectContainerLessons.get(key) ?? 0;
    });

    return {
      subjectLessons,
      subjectContainerLessons,
      additional,
      sumSubjectLessons,
      sumSubjectContainerLessons,
      sumAdditional,
      sumTotal,
    };
  }, [additionalTeachingLoadQueryResult, deputateData]);

  return (
    <Card
      header={{ headline: t('deputate.evaluate.allForSchoolyear', { year: currentSchoolYear?.shortName }) }}
      wrapperClass={styles['evaluate-planned-deputate-stats']}
      className={styles.content}
      contentPadding='none'
    >
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div>{t('deputate.deputateSubject')}</div>
          <div>{formatNumber(memoizedStats.sumSubjectLessons)}</div>
        </div>
        {[...memoizedStats.subjectContainerLessons.keys()].map((key) => {
          return (
            <div className={styles.stat} key={key}>
              <div>{t('deputate.evaluate.subjectContainer', { container: key })}</div>
              <div>{formatNumber(memoizedStats.sumSubjectContainerLessons.get(key) ?? 0.0)}</div>
            </div>
          );
        })}
        <div className={styles.stat}>
          <div>{t('deputate.deputateManually')}</div>
          <div>{formatNumber(memoizedStats.sumAdditional)}</div>
        </div>
        <div className={styles.stat}>
          <div>{t('common.sum')}</div>
          <div>{formatNumber(memoizedStats.sumTotal)}</div>
        </div>
      </div>
    </Card>
  );
});
